<template>
  <Dialog ref="dialog" :on-close="closeDialog" trigger-classes="w-full flex justify-center sm:justify-start focus:outline-none">
    <template #trigger>
      <ActionButton :primary="true" v-if="props.showButton">
        <template #icon>
          <DownloadIcon />
        </template>
        <template #title>{{ I18n.t("editor.download") }}</template>
      </ActionButton>
      <div class="flex gap-1 items-start justify-center cursor-wait" v-else>
        <div class="h-11 w-11 rounded-full !bg-secondary animate-pulse"></div>
      </div>
    </template>

    <template v-slot:content="{ closeDialog }">
      <div class="flex flex-col gap-y-2 items-center w-100 pt-3">
        <ButtonPlaceholder v-if="props.isProcessing"></ButtonPlaceholder>
        <Button v-else class="text-center" autofocus :loading="props.isProcessingPreviewDownload" :full-width="true" @click="props.downloadPreview">{{ I18n.t("editor.download") }}</Button>
        <div v-show="!(store.selectedImage.meta.previewWidth && store.selectedImage.meta.previewHeight)" class="mb-4">
          <div class="flex gap-1 items-center cursor-wait text-typo-tertiary">
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
            &times;
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
          </div>
        </div>
        <div v-show="store.selectedImage.meta.previewWidth && store.selectedImage.meta.previewHeight" class="d-block d-md-inline text-typo-tertiary mb-1">
          {{ store.selectedImage.meta.previewWidth }} &times; {{ store.selectedImage.meta.previewHeight }} px
          <span class="hover-tooltip" :data-tippy-content="`<strong>${I18n.t('image.good_quality')}</strong><br/>${I18n.t('image.up_to_025_megapixels')}`" data-tippy-placement="bottom">
            <InfoSmallIcon> </InfoSmallIcon>
          </span>
        </div>

        <template>
          <DownloadHdDialog :editor="props.editor" :download-hd="props.downloadHd" :enable-download-hd-button="props.enableDownloadHdButton" :is-processing="props.isProcessing" :is-processing-hd-download="props.isProcessingHdDownload" :is-processing-preview-download="props.isProcessingPreviewDownload" :refresh-preview-from-stage="props.refreshPreviewFromStage" :credits="props.credits" :load-credits-and-update-preview="props.loadCreditsAndUpdatePreview" />
        </template>
        <div v-show="isProcessing">
          <div class="flex gap-1 items-center cursor-wait text-typo-tertiary">
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
            &times;
            <div class="h-6 w-16 rounded-full !bg-secondary animate-pulse" />
          </div>
        </div>
        <div v-show="props.enableDownloadHdButton && !isProcessing" class="d-block d-md-inline center text-typo-tertiary">
          {{ store.selectedImage.meta.hdWidth }} &times; {{ store.selectedImage.meta.hdHeight }} px
          <span class="hover-tooltip" :data-tippy-content="`<strong>${I18n.t('image.best_quality')}</strong><br/>${I18n.t('image.up_to_25_megapixels')}`" data-tippy-placement="bottom">
            <InfoSmallIcon> </InfoSmallIcon>
          </span>
        </div>
        <span v-show="!props.enableDownloadHdButton && !isProcessing" class="text-typo-tertiary">
          {{ I18n.t("image.not_available") }}
          <span class="hover-tooltip" :data-tippy-content="`${I18n.t('image.please_upload_higher_resolution')}`" data-tippy-placement="bottom">
            <InfoSmallIcon> </InfoSmallIcon>
          </span>
        </span>

        <Button variant="secondary" class="text-center mt-3 w-100" v-if="shareSupported" @click="props.sharePreview"><ShareIcon class="mr-2" />{{ I18n.t("image.share") }}</Button>
      </div>
    </template>

    <template #mobile-actions>
      <Button @click="closeDialog">{{ I18n.t("editor.modal_close") }}</Button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import "@/src/i18n";

import { Dialog, Button, DownloadIcon, ActionButton, ButtonPlaceholder, ShareIcon, InfoSmallIcon } from "prism";
import DownloadHdDialog from "../prism/download_hd_dialog.vue";

import { useEditorStore } from "@/stores/editor_store";
import { inject, onMounted, ref } from "vue";
import { fileFromUri } from "@/modules/utils";

const store = useEditorStore();

interface MobileDownloadProps {
  editor: any;
  enableDownloadHdButton: Boolean;
  isProcessing: Boolean;
  isProcessingHdDownload: Boolean;
  isProcessingPreviewDownload: Boolean;
  credits: any;
  downloadHd: any;
  downloadPreview: any;
  sharePreview: any;
  refreshPreviewFromStage: any;
  loadCreditsAndUpdatePreview: any;
  preview: any;
  name: string;
  showButton: any;
}

const props = defineProps<MobileDownloadProps>();

const I18n = inject("I18n");

const emit = defineEmits<{
  (e: "closeDialog"): void;
  (e: "downloadPreview"): void;
  (e: "sharePreview"): void;
  (e: "downloadHd"): void;
}>();

const closeDialog = () => {
  emit("closeDialog");
};

const shareSupported = ref(false);

onMounted(async () => {
  const file = await fileFromUri(props.preview, props.name);
  const supported = file && (window.mobile_app || (navigator.canShare && navigator.canShare({ files: [file] })));
  shareSupported.value = supported;
});
</script>
<style>
*[style*="display: none"] {
  display: none !important;
}

.hover-tooltip > svg {
  display: inline;
}
</style>
